import {format, formatShort, implementLocaleSeparators, setCurrencyData, CurrencyData, getCurrencyData} from '@rubyplay/currency-formatter';
import {ICurrencyFormatter, IService, SystemComponentType, CurrencyConfiguration} from '@gongxigames/framework/core/types';
import {injectable} from '@gongxigames/framework/core/installer';
import {CoreDI} from '@gongxigames/framework/core/configs';

@injectable()
export class CurrencyFormatter implements ICurrencyFormatter, IService {
    public static NAME: symbol = CoreDI.CurrencyFormatter;
    public type: SystemComponentType = 'service';
    private _currencyCode!: string;
    private _secondaryCurrencyCode!: string;
    private _fractionSize!: number;
    private _localeCode!: string;

    public setCurrencyData(currencyCode: string, data: CurrencyData): void {
        setCurrencyData(currencyCode, data);
    }

    public getCurrencyData(currencyCode: string): CurrencyData|undefined {
        return getCurrencyData(currencyCode);
    }

    public setParameters(config: CurrencyConfiguration): void {
        this._currencyCode = config.currencyCode;
        this._fractionSize = config.fractionSize;
        this._localeCode = config.localeCode;

        if (config.secondaryCurrencyCode) {
            this._secondaryCurrencyCode = config.secondaryCurrencyCode;
        }
    }

    /**
     *
     * @param cents amount to format
     * @param useSecondary optional parameter to format in secondary currency.
     * can be used only if secondaryCurrencyCode is present in CurrencyFormatter class.
     * @returns formatted string
     */
    public formatCents(cents: number, useSecondary = false): string {
        return this.format(cents / this._fractionSize, useSecondary);
    }
    /**
     *
     * @param value amount to format
     * @param useSecondary optional parameter to format in secondary currency.
     * can be used only if secondaryCurrencyCode is present in CurrencyFormatter class.
     * @returns formatted string
     */
    public format(value: number, useSecondary = false): string {
        return format((useSecondary && this._secondaryCurrencyCode) ? this._secondaryCurrencyCode : this._currencyCode, value, this._localeCode);
    }

    /**
     * cuts 1000 to 'k' and 1000000 to 'm' sign
     */
    public formatShort(value: number, totalBet: number): string {
        return formatShort(value, totalBet, this._localeCode, this._currencyCode);
    }

    /**
     * formats number according to all currencies rules but without currency sign
     */
    public formatNumber(value: number | string): string {
        return implementLocaleSeparators(String(value), this._localeCode, this._currencyCode);
    }

    public get secondaryCurrencyCode(): string {
        return this._secondaryCurrencyCode;
    }

    public get currencyCode(): string {
        return this._currencyCode;
    }

    public set currencyCode(value: string) {
        this._currencyCode = value;
    }

    public get fractionSize(): number {
        return this._fractionSize;
    }

    public set fractionSize(value: number) {
        this._fractionSize = value;
    }

    public set localeCode(value: string) {
        this._localeCode = value;
    }
}
